import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Car,
  Clock,
  Instagram,
  Facebook,
  Twitter,
  Globe,
  BookOpen,
  MessageCircle,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import { SITE } from "@/data/site";

export default function Footer() {
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.address.full)}`;

  return (
    <footer className="bg-charcoal text-cream border-t border-gold/20" data-testid="site-footer">
      {/* Main Footer Content */}
      <div className="u-container py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column - Brand & Chef Profile & Contact */}
          <div className="lg:col-span-5 space-y-4 lg:pr-8 lg:border-r border-cream/10">
            {/* Logo Badge & Pill */}
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/" className="inline-block border border-gold/40 rounded-full p-1.5 h-14 w-14 overflow-hidden bg-ink/40 shrink-0">
                <img
                  src="/images/logo_new_white_clean.png"
                  alt="Chef Jomon's #VEEDU"
                  className="h-full w-full object-contain"
                  style={{ position: "relative", left: "12%", top: "8%" }}
                />
              </Link>
              <span className="font-body text-[10px] uppercase tracking-[0.25em] px-3.5 py-1.5 border border-gold/40 rounded-full text-gold bg-gold/5 font-medium">
                Kerala Heritage Cuisine
              </span>
            </div>

            {/* Chef Name & Title */}
            <div>
              <h3 className="font-display text-3xl md:text-4xl leading-tight text-cream">
                Chef Jomon
              </h3>
              <p className="font-body text-[11px] uppercase tracking-[0.28em] text-gold/80 font-medium mt-0.5">
                {SITE.chef.role}
              </p>
            </div>

            {/* Phone Contacts */}
            <div className="space-y-2 pt-1 font-body text-sm text-cream/80">
              <a href={`tel:${SITE.phones.director.replace(/\s+/g, "")}`} className="flex items-center gap-3 hover:text-gold transition-colors">
                <span className="w-7 h-7 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0 text-gold">
                  <Phone size={13} />
                </span>
                <span>{SITE.phones.director}</span>
              </a>
              <a href={`tel:${SITE.phones.restaurant.replace(/\s+/g, "")}`} className="flex items-center gap-3 hover:text-gold transition-colors">
                <span className="w-7 h-7 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0 text-gold">
                  <Phone size={13} />
                </span>
                <span>{SITE.phones.restaurant}</span>
              </a>
            </div>

            {/* Social Icons Row */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {SITE.social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="w-8 h-8 rounded-full border border-cream/20 flex items-center justify-center text-cream/75 hover:text-gold hover:border-gold transition-all duration-300 bg-ink/30"
                >
                  {s.label === "Instagram" && <Instagram size={14} />}
                  {s.label === "Facebook" && <Facebook size={14} />}
                  {s.label === "Wikipedia" && <BookOpen size={14} />}
                  {s.label === "Website" && <Globe size={14} />}
                  {s.label === "X" && <Twitter size={13} />}
                </a>
              ))}
            </div>
          </div>

          {/* Right Column - Visit VEEDU & Location Details */}
          <div className="lg:col-span-7 space-y-5">
            <div>
              <p className="u-eyebrow text-gold mb-1">Come Home to the Table</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide text-cream">
                VISIT VEEDU
              </h2>
            </div>

            {/* Info Items List */}
            <div className="space-y-3 font-body text-sm text-cream/80">
              {/* Address */}
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0 text-gold mt-0.5">
                  <MapPin size={14} />
                </span>
                <div className="leading-relaxed">
                  <span className="text-cream/90 font-medium">Address: </span>
                  <span>{SITE.address.full} </span>
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-gold hover:underline ml-1 text-xs uppercase tracking-wider font-medium"
                  >
                    Open in Google Maps <ArrowUpRight size={11} />
                  </a>
                </div>
              </div>

              {/* Parking */}
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0 text-gold mt-0.5">
                  <Car size={14} />
                </span>
                <div className="leading-relaxed">
                  <span className="text-cream/90 font-medium">Parking: </span>
                  <span>Nearest parking: {SITE.parking}</span>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0 text-gold mt-0.5">
                  <Clock size={14} />
                </span>
                <div className="leading-relaxed">
                  <span className="text-cream/90 font-medium">Hours: </span>
                  <span>Lunch 12–3 · Dinner 7–11 · Closed Mondays</span>
                </div>
              </div>
            </div>

            {/* Action Button & Script Quote Row */}
            <div className="pt-3 border-t border-cream/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/contact#enquire"
                className="btn-veedu border-gold text-gold hover:bg-gold hover:text-ink transition-colors px-6 py-3 text-xs tracking-[0.25em]"
              >
                RESERVE TABLE <ArrowRight size={14} />
              </Link>
              <p className="font-script text-gold text-xl md:text-2xl italic">
                "Some places you visit, some places you feel. This is home."
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Sub-Footer Bar */}
      <div className="bg-ink border-t border-gold/20 py-3.5">
        <div className="u-container flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
          {/* Words divided by bars */}
          <div className="flex flex-wrap justify-center items-center gap-2.5 font-body text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-cream/60">
            <span>VIBRANT</span>
            <span className="text-gold/40">|</span>
            <span>EARTHY</span>
            <span className="text-gold/40">|</span>
            <span>ENTICING</span>
            <span className="text-gold/40">|</span>
            <span>DELECTABLE</span>
            <span className="text-gold/40">|</span>
            <span>UMAMI</span>
          </div>

          {/* Copyright */}
          <p className="font-body text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-cream/40">
            © {new Date().getFullYear()} CHEF JOMON'S VEEDU. ALL RIGHTS RESERVED.
          </p>
        </div>
      </div>
    </footer>
  );
}
